/**
 * ConfirmDialog — generic confirm modal (overlay + card) for destructive actions.
 * For Media Devoted | Created By Ciro Autiero
 * File edited by By James Alexandrov
 */

import { FC, ReactNode } from "react";
import { createPortal } from "react-dom";

import { Button } from "./Button";
import { useModalPortalSurface } from "./ModalPortalSurfaceContext";
import { backdropClick } from "./backdropClick";
import { useEscapeToClose } from "./useEscapeToClose";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  /** Disables ONLY the confirm button — e.g. an in-dialog choice not made yet. */
  confirmDisabled?: boolean;
  /** Max-width class for the card (default "max-w-md"). */
  widthClass?: string;
  /** Hide Cancel when there is nothing to cancel — e.g. a results view whose
   *  only action is Close. Two buttons that do the same thing make readers
   *  hesitate. Backdrop click still closes via `onCancel`. */
  hideCancel?: boolean;
  /** A second way forward, between Cancel and the confirm (outline, never
   *  destructive) — e.g. "Queue 1 for a spot" beside "Move to live". */
  secondaryAction?: { label: string; onClick: () => void };
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  destructive = false,
  busy = false,
  confirmDisabled = false,
  widthClass = "max-w-md",
  hideCancel = false,
  secondaryAction,
  onConfirm,
  onCancel,
}) => {
  // A confirm nested in a Radix dialog (the drawer) portals INTO that
  // dialog's element so its FocusScope keeps treating it as inside — a body
  // portal gets its focus reclaimed (untypeable inputs, tab escapes).
  const host = useModalPortalSurface();
  // Escape cancels the topmost dialog — the keyboard twin of the backdrop
  // click (hook runs before the early return to keep hook order stable).
  useEscapeToClose(open, busy, onCancel);
  if (!open) return null;
  // Portal to <body> so the modal escapes the table's overflow/scroll context
  // (otherwise a row-level confirm renders constrained + horizontally scrolled).
  //
  // pointer-events-auto is REQUIRED, not cosmetic. A Radix Dialog (our Drawer)
  // sets `pointer-events: none` on <body> while it is open, and this portals to
  // <body> — so inside a drawer the confirm painted normally but neither button
  // could be clicked and it read as a frozen dialog. Outside a drawer <body> is
  // already interactive, so this is a no-op there.
  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[200] flex items-center justify-center bg-background/95 p-4 backdrop-blur-sm"
      onClick={backdropClick(busy ? undefined : onCancel)}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`relative max-h-[90vh] w-full ${widthClass} overflow-y-auto overflow-x-hidden rounded-xl border border-border bg-card shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="text-base font-semibold">{title}</h3>
        </div>
        <div className="p-4 text-sm text-muted-foreground">{description}</div>
        <div className="flex justify-end gap-2 border-t border-border bg-muted/10 px-4 py-3">
          {hideCancel ? null : (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </Button>
          )}
          {secondaryAction ? (
            <Button
              variant="outline"
              size="sm"
              onClick={secondaryAction.onClick}
              disabled={busy}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
          <Button
            variant={destructive ? "destructive" : "default"}
            size="sm"
            onClick={onConfirm}
            loading={busy}
            disabled={busy || confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    host ?? document.body,
  );
};

export default ConfirmDialog;
